import { readingHtml, serverMediatedPage } from '../shared/reading_request';
import PopoverService from './popover_service';
import { loadTransliteration, usesSegmentedReadingAid, usesTransliteration } from '../reading_aids/reading_aid_languages';
import { nativeMatchesTarget, sameLanguageNoteHtml } from './same_language';

class BottomUpPopoverService extends PopoverService {
  constructor(translationService, language, contentId, contentType, csrfToken, premiumAccount = 'false', hideTranslationSentence = false, playerService = null, voice = null, voiceProvider = 'elevenlabs') {
    super(translationService, language, contentId, contentType, csrfToken, premiumAccount, hideTranslationSentence, playerService, voice, voiceProvider);
    this.bottomUpWindow = null;
  }

  shouldHideExtendedFeatures() {
    return usesSegmentedReadingAid(this.language);
  }

  // The word with its romanization under it. `annotated` is the tapped element
  // carrying data-translit; its reading is reused when it is still the word
  // shown (the infinitive replaces it, and is romanized on its own).
  async transliteratedWordHtml(word, annotated) {
    try {
      const dom = await loadTransliteration();
      const reading = annotated.textContent.trim() === word
        ? annotated.getAttribute('data-translit')
        : dom.readingForWord(this.language, word);
      return reading ? dom.translitWordHtml(word, reading) : null;
    } catch (_error) {
      return null;
    }
  }

  showLoadingPopover(wordElement) {
    const loadingContent = `
      <div class="bottom-up-content">
        <div class="skeleton skeleton-text"></div>
        <div class="skeleton skeleton-text skeleton-text__body"></div>
        <div class="mt-2 skeleton skeleton-footer"></div>
        <div class="skeleton skeleton-footer"></div>
      </div>
    `;
    this.showBottomUpWindow(loadingContent);
  }

  async updatePopoverContent(wordElement, data, phraseSelected = false, readingAidService = null) {
    if (serverMediatedPage() && data.error) {
      wordElement.classList.remove('loading');
      this.showBottomUpWindow(`<div class="bottom-up-content"><p role="alert" class="text-danger">${readingHtml(data.error)}</p></div>`);
      return;
    }
    this.currentWord = data.word;
    this.translatedWord = data.translation;
    this.phraseSelected = phraseSelected;
    this.translatedSentence = data.translated_sentence ? data.translated_sentence : '';

    let fullTranslationSentence = I18n.t('javascript.empty_states.no_sentence_given');
    this.contextSentence = data.context_sentence ? data.context_sentence : '';

    let displayWordInfo = '';
    let infinitiveSection = '';

    if (data.word_info) {
      let wordInfo = data.word_info;
      displayWordInfo = wordInfo.human_readable || '';
      
      // Handle verb case - add infinitive option (only if lemma is different from current word)
      if ((wordInfo.pos === 'Verb' || wordInfo.pos === 'Auxiliary') && wordInfo.lemma !== this.currentWord) {
        infinitiveSection = `
          <div class="mt-2">
            <a class="popover-collapse-link js-translate-infinitive" href="#" data-infinitive="${readingHtml(wordInfo.lemma)}">
              Translate '${readingHtml(wordInfo.lemma)}'
            </a>
            <div class="collapse js-infinitive-translation-card"></div>
          </div>
        `;
      }

      if (data.translated_infinitive) {
        this.translatedWord = data.translated_infinitive;
        this.currentWord = data.word_infinitive;
      } else if (data.translated_word) {
        this.translatedWord = data.translated_word;
      }

    } else {
      if (data.translated_infinitive) {
        this.translatedWord = data.translated_infinitive;
        this.currentWord = data.word_infinitive;
      } else if (data.translated_word) {
        this.translatedWord = data.translated_word;
      }

      if (wordElement.dataset.wordInfo) {
        let wordInfo = JSON.parse(wordElement.dataset.wordInfo);

        if (wordInfo.human_readable) {
          displayWordInfo = wordInfo.human_readable;
        }
      }
    }

    if (this.contextSentence !== '' && this.translatedSentence !== '' && this.translatedSentence !== null) {
      let boldedWord = `<strong>${this.currentWord}</strong>`;
      if (!serverMediatedPage()) this.contextSentence = this.contextSentence.replace(this.currentWord, boldedWord);
      fullTranslationSentence = `${this.contextSentence} = ${this.translatedSentence}`;
    }

    // add japanese and chinese to the furigana
    if (readingAidService && usesSegmentedReadingAid(this.language)) {
      // this could be an array of objects
      this.currentProcessedText = await readingAidService.processText(this.currentWord, this.language);
      if (this.currentProcessedText.length === 1) {
        this.currentProcessedText = this.currentProcessedText[0].html;
      } else if (this.currentProcessedText.length > 1) {
        this.currentProcessedText = this.currentProcessedText.map(item => item.html).join('');
      } else {
        this.currentProcessedText = this.currentWord;
      }
    }
    // LAN-870: the tapped word carries data-translit only when the learner has
    // the Greek or Korean transliteration aid on. Show the reading under it.
    if (usesTransliteration(this.language)) {
      const annotated = wordElement && wordElement.closest && wordElement.closest('[data-translit]');
      this.currentProcessedText = annotated ? await this.transliteratedWordHtml(this.currentWord, annotated) : null;
    }
    const currentWordContent = this.currentProcessedText ? this.currentProcessedText : this.currentWord;

    let popoverContent = `
      <div class="bottom-up-content">
        <div class="d-flex justify-content-between align-items-center">
          ${data.error ? `<h5 class="text-danger">${readingHtml(data.error)}</h5>` : `<h5><span>${readingHtml(currentWordContent)}</span> = ${readingHtml(this.translatedWord)}</h5>`}
        </div>

      ${this.phraseSelected ? '' : `
        ${infinitiveSection !== '' ?
          infinitiveSection
          :
          (displayWordInfo ? `<p class="text-muted">${readingHtml(displayWordInfo)}</p>` : '')
        }
        ${!this.shouldHideExtendedFeatures() ?
          this.premiumAccount == 'true' ?
            `<div class="mt-2">
              <a class="popover-collapse-link js-translate-multiple" href="#">${I18n.t('javascript.popover.all_meanings')}</a>
              <div class="collapse js-multiple-translation-card"></div>
            </div>`
            :
            `<div class="mt-2">
              <a class="js-subscriptions-modal subscription-link" href="#">${I18n.t('javascript.popover.all_meanings')}</a>
            </div>`
          : ''
        }

        ${!this.hideTranslationSentence && !this.shouldHideExtendedFeatures() ?
          `<div class="mt-2">
            <a class="popover-collapse-link js-translate-sentence" href="#">${I18n.t('javascript.popover.translate_sentence')}</a>
            <div class="collapse js-translate-sentence-card"></div>
          </div>`
          : ''}
        
        ${serverMediatedPage() ? '' : this.premiumAccount == 'true' && !this.shouldHideExtendedFeatures() ?
          `<div class="mt-2">
              <a class="popover-collapse-link js-usage-examples" href="#">${I18n.t('javascript.popover.usage_examples')}</a>
              <div class="collapse js-usage-examples-card"></div>
          </div>
          <div class="mt-2">
              <a class="popover-collapse-link js-phrase-examples" href="#">${I18n.t('javascript.popover.phrases')}</a>
              <div class="collapse js-phrase-examples-card"></div>
          </div>
          <div class="mt-2">
              <a class="popover-collapse-link js-similar-words" href="#">${I18n.t('javascript.popover.similar_words')}</a>
              <div class="collapse js-similar-words-card"></div>
          </div>
          <div class="mt-2">
              <a class="popover-collapse-link js-definition" href="#">${I18n.t('javascript.popover.definition_in', {language: this.language.charAt(0).toUpperCase() + this.language.slice(1)})}</a>
              <div class="collapse js-definition-card"></div>
          </div>`
          :
          !this.shouldHideExtendedFeatures() ?
          `<div class="mt-2">
              <a class="js-subscriptions-modal subscription-link" href="#">${I18n.t('javascript.popover.usage_examples')}</a>
          </div>
          <div class="mt-2">
              <a class="js-subscriptions-modal subscription-link" href="#">${I18n.t('javascript.popover.phrases')}</a>
          </div>
          <div class="mt-2">
              <a class="js-subscriptions-modal subscription-link" href="#">${I18n.t('javascript.popover.similar_words')}</a>
          </div>
          <div class="mt-2">
              <a class="js-subscriptions-modal subscription-link" href="#">${I18n.t('javascript.popover.definition_in', {language: this.language.charAt(0).toUpperCase() + this.language.slice(1)})}</a>
          </div>`
          : ''
        }
      `}

        ${nativeMatchesTarget(this.translationService?.targetLanguage, this.language) ? sameLanguageNoteHtml() : ''}

        <div class="btn-group mt-3 custom-btn-group" role="group" aria-label="Basic example">
          ${this.premiumAccount == 'true' ?
            `<a href="#" class="btn btn-primary js-flashcard-btn flex-grow-1">
                <i class="fa fa-plus plus-icon" aria-hidden="true"></i>
                <i class="fa fa-check check-icon d-none" aria-hidden="true"></i>
            </a>`
            :
            `<a href="#" class="btn btn-primary js-subscriptions-modal flex-grow-1">
                <i class="fa fa-plus plus-icon" aria-hidden="true"></i>
                <i class="fa fa-check check-icon d-none" aria-hidden="true"></i>
            </a>`
          }
          
          ${serverMediatedPage() ? '' : `<a href="#" class="btn btn-secondary js-play-audio-btn">
            <i class="fa fa-volume-up" aria-hidden="true"></i>
          </a>`}
        </div>
      </div>`;

    this.showBottomUpWindow(popoverContent);
    wordElement.classList.add('active');
  }

  showBottomUpWindow(content) {
    if (!this.bottomUpWindow) {
      this.createBottomUpWindow();
    }
    this.bottomUpWindow.querySelector('.bottom-up-content').innerHTML = content;
    this.bottomUpWindow.classList.add('show');
    document.body.classList.add('bottom-up-open');
  }

  createBottomUpWindow() {
    this.bottomUpWindow = document.createElement('div');
    this.bottomUpWindow.className = 'bottom-up-window';
    this.bottomUpWindow.innerHTML = `
      <div class="bottom-up-content"></div>
      <button class="bottom-up-close btn-close">&times;</button>
    `;
    document.body.appendChild(this.bottomUpWindow);

    this.bottomUpWindow.querySelector('.bottom-up-close').addEventListener('click', () => {
      this.hideBottomUpWindow();
    });
  }

  hideBottomUpWindow() {
    if (this.bottomUpWindow) {
      this.bottomUpWindow.classList.remove('show');
      document.body.classList.remove('bottom-up-open');
      const activeWords = document.getElementsByClassName('active');
      for (let i = 0; i < activeWords.length; i++) {
        activeWords[i].classList.remove('active');
      }
    }
  }

  disposePopover(element) {
    this.hideBottomUpWindow();
    element.classList.remove('active');
  }

  disposeAllPopovers() {
    this.hideBottomUpWindow();
    const activeWords = document.getElementsByClassName('active');
    for (let i = 0; i < activeWords.length; i++) {
      activeWords[i].classList.remove('active');
    }
  }

  setupEventListeners() {
    super.setupEventListeners();

    // This event listener is specific to the bottom-up popover and should be kept
    document.addEventListener('click', (e) => {
      if (this.bottomUpWindow && !this.bottomUpWindow.contains(e.target) && !e.target.classList.contains('active')) {
        this.hideBottomUpWindow();
      }
    });
  }
}

export default BottomUpPopoverService;
